// Weapon attachment geometry. Attachments mount on the rail (optics), the barrel tip (muzzle devices),
// the underside of the handguard (grips, laser) and replace the stock (light_stock). Barrel and magazine
// variants change the weapon's own geometry, so they are exposed as parameters (barrelDelta, magVariant)
// that the weapon builders in weapons.ts consume.
//
// Weapon local space: +X right, +Y up, -Z forward (muzzle), origin at the trigger.
// Material keys used here: body, trim, metal, accent, lens, emit (see materials/materials.ts).

import { MeshBuilder } from "./geom";

export type AttachmentSlotName = "optic" | "muzzle" | "barrel" | "mag" | "grip";
export type OpticId = "red_dot" | "holo" | "scope_2x" | "scope_4x";
export type MuzzleId = "compensator" | "suppressor" | "muzzle_brake";
export type BarrelId = "long_barrel" | "short_barrel";
export type MagId = "ext_mag" | "fast_mag";
export type GripId = "vertical_grip" | "angled_grip" | "laser" | "light_stock";

/** At most one attachment per slot. */
export interface AttachmentSet {
  optic?: OpticId;
  muzzle?: MuzzleId;
  barrel?: BarrelId;
  mag?: MagId;
  grip?: GripId;
}

const SLOT_OF: Readonly<Record<string, AttachmentSlotName>> = {
  red_dot: "optic",
  holo: "optic",
  scope_2x: "optic",
  scope_4x: "optic",
  compensator: "muzzle",
  suppressor: "muzzle",
  muzzle_brake: "muzzle",
  long_barrel: "barrel",
  short_barrel: "barrel",
  ext_mag: "mag",
  fast_mag: "mag",
  vertical_grip: "grip",
  angled_grip: "grip",
  laser: "grip",
  light_stock: "grip",
};

export const ATTACHMENT_IDS: readonly string[] = Object.keys(SLOT_OF);

/** Slot of an attachment id, or null when the id is unknown. */
export function attachmentSlot(id: string): AttachmentSlotName | null {
  return SLOT_OF[id] ?? null;
}

/**
 * Weapons that wear a red dot in the first-person view when the loadout picks no optic: the rifle,
 * the submachine gun, the burst rifle and the marksman rifle. Pistols, the revolver and the shotgun
 * keep iron sights; the sniper rifle has its built-in scope. This is a look only: the sim's stats
 * (ADS FOV, spread) change only for an optic the loadout equips.
 */
const DEFAULT_RED_DOT: Readonly<Record<string, true>> = {
  kr7: true,
  vx9: true,
  br3: true,
  dm2: true,
};

/** `attachments` plus the default red dot when the weapon wears one and no optic is chosen. */
export function withDefaultOptic(
  weaponId: string,
  attachments: readonly string[],
): string[] {
  const hasOptic = attachments.some((a) => SLOT_OF[a] === "optic");
  if (hasOptic || DEFAULT_RED_DOT[weaponId] !== true)
    return attachments.slice();
  return [...attachments, "red_dot"];
}

/** True when aiming looks through a dot sight (red dot or holo, chosen or default): the HUD draws a dot reticle. */
export function hasDotSight(
  weaponId: string,
  attachments: readonly string[],
): boolean {
  return withDefaultOptic(weaponId, attachments).some(
    (a) => a === "red_dot" || a === "holo",
  );
}

/**
 * Attachments each weapon accepts, in unlock order. Mirrors `WeaponId::allowed_attachments` in
 * crates/gr-core/src/ids.rs (used by the model viewer and the tests).
 */
export const WEAPON_ATTACHMENTS: Readonly<Record<string, readonly string[]>> = {
  vx9: [
    "red_dot",
    "laser",
    "compensator",
    "fast_mag",
    "short_barrel",
    "holo",
    "ext_mag",
    "angled_grip",
    "suppressor",
    "light_stock",
    "long_barrel",
    "muzzle_brake",
  ],
  kr7: [
    "red_dot",
    "vertical_grip",
    "compensator",
    "ext_mag",
    "holo",
    "long_barrel",
    "scope_2x",
    "fast_mag",
    "muzzle_brake",
    "angled_grip",
    "suppressor",
    "laser",
    "short_barrel",
    "light_stock",
  ],
  br3: [
    "red_dot",
    "compensator",
    "holo",
    "vertical_grip",
    "scope_2x",
    "ext_mag",
    "long_barrel",
    "fast_mag",
    "scope_4x",
    "suppressor",
    "angled_grip",
    "muzzle_brake",
  ],
  hx12: [
    "laser",
    "short_barrel",
    "long_barrel",
    "ext_mag",
    "red_dot",
    "fast_mag",
    "angled_grip",
    "light_stock",
    "suppressor",
  ],
  dm2: [
    "red_dot",
    "scope_2x",
    "long_barrel",
    "ext_mag",
    "muzzle_brake",
    "scope_4x",
    "fast_mag",
    "vertical_grip",
    "holo",
    "suppressor",
    "angled_grip",
    "compensator",
  ],
  lx44: [
    "long_barrel",
    "short_barrel",
    "fast_mag",
    "ext_mag",
    "suppressor",
    "light_stock",
    "angled_grip",
    "muzzle_brake",
  ],
  p17: [
    "red_dot",
    "laser",
    "ext_mag",
    "compensator",
    "fast_mag",
    "suppressor",
    "long_barrel",
    "short_barrel",
  ],
  vp4: [
    "laser",
    "ext_mag",
    "red_dot",
    "compensator",
    "fast_mag",
    "suppressor",
    "short_barrel",
    "angled_grip",
  ],
  hc50: [
    "red_dot",
    "long_barrel",
    "laser",
    "fast_mag",
    "compensator",
    "holo",
    "short_barrel",
  ],
  knife: [],
};

/** Picks the first valid attachment per slot; unknown ids are ignored. */
export function parseAttachments(ids: readonly string[]): AttachmentSet {
  const set: AttachmentSet = {};
  for (const id of ids) {
    switch (SLOT_OF[id]) {
      case "optic":
        set.optic ??= id as OpticId;
        break;
      case "muzzle":
        set.muzzle ??= id as MuzzleId;
        break;
      case "barrel":
        set.barrel ??= id as BarrelId;
        break;
      case "mag":
        set.mag ??= id as MagId;
        break;
      case "grip":
        set.grip ??= id as GripId;
        break;
      default:
        break;
    }
  }
  return set;
}

/** Change of the exposed barrel length in metres (positive = muzzle moves forward). */
export function barrelDelta(set: AttachmentSet): number {
  if (set.barrel === "long_barrel") return 0.1;
  if (set.barrel === "short_barrel") return -0.06;
  return 0;
}

export interface MagVariant {
  /** Multiplier of the magazine body length. */
  lengthScale: number;
  /** Fast mag: coloured pull tab and base plate. */
  fast: boolean;
}

export function magVariant(set: AttachmentSet): MagVariant {
  return {
    lengthScale: set.mag === "ext_mag" ? 1.45 : 1,
    fast: set.mag === "fast_mag",
  };
}

/** Weapon attachment points, all in weapon local space. */
export interface Mounts {
  /** Y of the top of the optic rail / slide. */
  railTop: number;
  /** Z of the optic centre. */
  opticZ: number;
  /** Barrel axis height and radius. */
  barrelY: number;
  barrelR: number;
  /** Z of the barrel tip BEFORE any muzzle device (already includes the barrel variant). */
  muzzleZ: number;
  /** Underside of the handguard/frame where under-barrel accessories hang. */
  underY: number;
  underZ: number;
  /** Where a stock would start (rear of the receiver); null = the weapon has no stock. */
  stockZ: number | null;
  stockY: number;
}

export interface OpticResult {
  /** Rear window of the optic on the sight line (becomes the weapon's sightAnchor). */
  y: number;
  z: number;
  /** Draws the scope overlay at full ADS. */
  scope: boolean;
}

/** Builds the optic on the rail and returns where the eye looks through it. */
export function buildOptic(
  b: MeshBuilder,
  id: OpticId,
  m: Mounts,
): OpticResult {
  const y0 = m.railTop;
  const z0 = m.opticZ;
  switch (id) {
    case "red_dot":
      // Thin frame (2.5 mm walls, 3 mm top and bottom bars) around a 28 x 30 mm window, so the
      // housing hardly shows around the dot at full ADS.
      b.at([0, y0, z0], () => {
        b.box("trim", [0.022, 0.016, 0.046], [0, 0.008, 0]);
        b.box("trim", [0.033, 0.003, 0.05], [0, 0.0175, 0]);
        b.box("trim", [0.0025, 0.034, 0.05], [0.01525, 0.034, 0]);
        b.box("trim", [0.0025, 0.034, 0.05], [-0.01525, 0.034, 0]);
        b.box("trim", [0.033, 0.003, 0.05], [0, 0.0505, 0], { bevel: 0.001 });
        b.box("lens", [0.028, 0.03, 0.0015], [0, 0.034, 0]);
        b.box("emit", [0.004, 0.004, 0.002], [0, 0.036, -0.0015]);
      });
      return { y: y0 + 0.034, z: z0 + 0.035, scope: false };

    case "holo":
      b.at([0, y0, z0], () => {
        b.box("trim", [0.034, 0.012, 0.09], [0, 0.006, 0]);
        b.box("trim", [0.006, 0.042, 0.09], [0.0185, 0.033, 0]);
        b.box("trim", [0.006, 0.042, 0.09], [-0.0185, 0.033, 0]);
        b.box("trim", [0.043, 0.008, 0.092], [0, 0.058, 0], { bevel: 0.002 });
        b.box("lens", [0.03, 0.032, 0.002], [0, 0.034, -0.044]);
        b.box("lens", [0.03, 0.032, 0.002], [0, 0.034, 0.044]);
        b.lathe(
          "emit",
          [
            [0.0065, -0.0008],
            [0.0085, -0.0008],
            [0.0085, 0.0008],
            [0.0065, 0.0008],
          ],
          8,
          [0, 0.036, -0.04],
          { axis: "z" },
        );
        b.box("emit", [0.003, 0.003, 0.002], [0, 0.036, -0.04]);
      });
      return { y: y0 + 0.034, z: z0 + 0.046, scope: false };

    case "scope_2x":
      b.at([0, y0, z0], () => {
        b.box("trim", [0.034, 0.05, 0.016], [0, 0.025, -0.04]);
        b.box("trim", [0.034, 0.05, 0.016], [0, 0.025, 0.04]);
        b.cyl("metal", "z", 0.15, 0.0185, 0.0185, [0, 0.047, 0]);
        b.cyl("metal", "z", 0.04, 0.029, 0.0185, [0, 0.047, -0.095]);
        b.cyl("metal", "z", 0.035, 0.0185, 0.026, [0, 0.047, 0.0925]);
        b.cyl("lens", "z", 0.002, 0.026, 0.026, [0, 0.047, -0.116], {
          caps: true,
        });
        b.cyl("lens", "z", 0.002, 0.022, 0.022, [0, 0.047, 0.11]);
        b.box("metal", [0.016, 0.012, 0.016], [0, 0.073, 0]);
        b.box("metal", [0.012, 0.016, 0.012], [0.025, 0.047, 0]);
      });
      return { y: y0 + 0.047, z: z0 + 0.111, scope: false };

    case "scope_4x":
      b.at([0, y0, z0], () => {
        b.box("trim", [0.036, 0.05, 0.018], [0, 0.025, -0.06]);
        b.box("trim", [0.036, 0.05, 0.018], [0, 0.025, 0.06]);
        b.cyl("metal", "z", 0.22, 0.02, 0.02, [0, 0.05, 0], { segments: 10 });
        b.cyl("metal", "z", 0.06, 0.034, 0.02, [0, 0.05, -0.14], {
          segments: 10,
        });
        b.cyl("metal", "z", 0.04, 0.02, 0.03, [0, 0.05, 0.13], {
          segments: 10,
        });
        b.cyl("lens", "z", 0.002, 0.031, 0.031, [0, 0.05, -0.171]);
        b.cyl("lens", "z", 0.002, 0.026, 0.026, [0, 0.05, 0.151]);
        b.box("metal", [0.018, 0.014, 0.018], [0, 0.082, -0.02]);
        b.box("metal", [0.014, 0.018, 0.014], [0.03, 0.05, -0.02]);
        b.cyl("metal", "x", 0.02, 0.01, 0.01, [-0.03, 0.05, -0.1], {
          segments: 6,
        });
        b.box("accent", [0.021, 0.004, 0.012], [0, 0.0715, 0.07]);
      });
      return { y: y0 + 0.05, z: z0 + 0.151, scope: true };
  }
}

/** Builds a muzzle device at the barrel tip. Returns the new muzzle Z (where the flash/tracer starts). */
export function buildMuzzle(b: MeshBuilder, id: MuzzleId, m: Mounts): number {
  const r = m.barrelR;
  switch (id) {
    case "compensator":
      b.at([0, m.barrelY, m.muzzleZ], () => {
        b.cyl("metal", "z", 0.05, r * 1.7, r * 1.7, [0, 0, -0.025]);
        b.box("trim", [r * 3.5, r * 0.6, 0.006], [0, 0, -0.02]);
        b.box("trim", [r * 3.5, r * 0.6, 0.006], [0, 0, -0.034]);
      });
      return m.muzzleZ - 0.05;
    case "suppressor":
      b.at([0, m.barrelY, m.muzzleZ], () => {
        b.cyl("trim", "z", 0.17, r * 2.3, r * 2.3, [0, 0, -0.085], {
          segments: 10,
        });
        b.cyl("metal", "z", 0.012, r * 2.45, r * 2.45, [0, 0, -0.03]);
        b.cyl("metal", "z", 0.012, r * 2.45, r * 2.45, [0, 0, -0.14]);
      });
      return m.muzzleZ - 0.17;
    case "muzzle_brake":
      b.at([0, m.barrelY, m.muzzleZ], () => {
        b.cyl("metal", "z", 0.055, r * 1.6, r * 1.6, [0, 0, -0.0275]);
        b.box("trim", [r * 3.5, r * 1.2, 0.008], [0, 0, -0.022]);
        b.box("trim", [r * 1.2, r * 3.5, 0.008], [0, 0, -0.038]);
        b.cyl("metal", "z", 0.008, r * 2.0, r * 2.0, [0, 0, -0.012]);
      });
      return m.muzzleZ - 0.055;
  }
}

/** Under-barrel grip / laser. light_stock is handled by buildLightStock. */
export function buildGrip(b: MeshBuilder, id: GripId, m: Mounts): void {
  switch (id) {
    case "vertical_grip":
      b.at([0, m.underY, m.underZ], () => {
        b.box("trim", [0.024, 0.014, 0.034], [0, -0.007, 0]);
        b.box("trim", [0.026, 0.07, 0.03], [0, -0.049, 0], { bevel: 0.004 });
        b.box("accent", [0.028, 0.006, 0.032], [0, -0.078, 0]);
      });
      return;
    case "angled_grip":
      b.at([0, m.underY, m.underZ], () => {
        b.box("trim", [0.024, 0.012, 0.04], [0, -0.006, 0]);
        b.prism(
          "trim",
          [
            [0.016, -0.01],
            [-0.016, -0.01],
            [-0.05, -0.06],
            [-0.014, -0.064],
          ],
          0.026,
          [0, 0, 0],
        );
      });
      return;
    case "laser":
      b.at([0, m.underY, m.underZ], () => {
        b.box("metal", [0.026, 0.03, 0.06], [0, -0.015, 0], { bevel: 0.004 });
        b.box("trim", [0.018, 0.018, 0.006], [0, -0.015, -0.032]);
        b.box("emit", [0.008, 0.008, 0.004], [0, -0.015, -0.0345]);
        b.box("accent", [0.028, 0.006, 0.02], [0, -0.032, 0.005]);
      });
      return;
    case "light_stock":
      return;
  }
}

/** Skeletal lightweight stock that replaces the weapon's regular stock. */
export function buildLightStock(b: MeshBuilder, m: Mounts): void {
  if (m.stockZ === null) return;
  b.at([0, m.stockY, m.stockZ], () => {
    b.box("metal", [0.012, 0.012, 0.2], [0, 0.015, 0.1]);
    b.box("metal", [0.01, 0.01, 0.2], [0, -0.035, 0.1], { rot: [0.12, 0, 0] });
    b.box("metal", [0.01, 0.06, 0.01], [0, -0.01, 0.06]);
    b.box("trim", [0.04, 0.1, 0.014], [0, -0.015, 0.208], { bevel: 0.003 });
  });
}
